import { getLocalContentEntity } from './content-lifecycle/store';
import { completePrayerDocument } from './content-lifecycle/contracts';
import type { PrayerAttemptV1, PrayerIntent, PrayerLocalCommitResult } from '@/types/prayer-command';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import { authorPrayerAttempt, registerPrayerAttempt, revisePrayerAttempt, type ContentLifecycleAuthExpectation } from './content-lifecycle/store';
import { getCurrentContentLifecycleContext, syncContentLifecycle } from './content-lifecycle/sync';
import { getAuthEpoch, getAuthUserId } from './keepsake/epochs';
import { useAdiaStore } from './store';

export function prayerEditorAuth(): ContentLifecycleAuthExpectation {
  // An empty captured owner is an inert signed-out editor. Rendering read UI
  // remains safe, and this capture can never gain authority on a later login.
  return { ownerId: getAuthUserId() ?? '', authEpoch: getAuthEpoch() };
}

export function prayerEditorIsCurrent(auth: ContentLifecycleAuthExpectation): boolean {
  return Boolean(auth.ownerId) && getAuthUserId() === auth.ownerId && getAuthEpoch() === auth.authEpoch;
}

/** A session retains its attempt before commit, including failures. New text is
 * an exact revision of that attempt, never a second append identity. */
export class PrayerAuthoringSession {
  attempt: PrayerAttemptV1 | null = null;
  private committed = false;
  private saving = false;
  readonly auth: ContentLifecycleAuthExpectation;
  constructor(auth: ContentLifecycleAuthExpectation) { this.auth = auth; }
  async save(prayerId: string, intent: PrayerIntent,
    recoveredAudio: readonly RecoveredAudioCommitClaim[] = []): Promise<PrayerLocalCommitResult> {
    if (this.saving) throw new Error('prayer-attempt-in-flight');
    this.saving = true;
    try {
      if (this.committed) return { kind: 'not-pending' };
      if (!this.auth.ownerId) throw new Error('content-sync-not-authenticated');
      const context = await getCurrentContentLifecycleContext(this.auth);
      if (!this.attempt) this.attempt = await registerPrayerAttempt(context, prayerId, intent, recoveredAudio);
      else if (JSON.stringify(this.attempt.intent) !== JSON.stringify(intent) || JSON.stringify(this.attempt.recoveredAudio) !== JSON.stringify(recoveredAudio)) {
        this.attempt = await revisePrayerAttempt(context, this.attempt, intent, recoveredAudio);
      }
      if (!this.attempt) return { kind: 'not-pending' };
      const result = await authorPrayerAttempt(context, this.attempt);
      if (result.kind === 'committed') {
        this.committed = true;
        publishPrayerCommit(result);
      }
      return result;
    } finally { this.saving = false; }
  }
}

/** Every operation below is postcommit. Neither observers nor sync can reject
 * the successful IDB transaction or repopulate an already-saved editor. */
export function publishPrayerCommit(result: Extract<PrayerLocalCommitResult, { kind: 'committed' }>): void {
  void getLocalContentEntity(result.ownerId, 'prayer', result.prayer.id).then(current => {
    if (!current?.visibleDocument || JSON.stringify(current.visibleDocument) !== JSON.stringify(completePrayerDocument(result.prayer))) return;
    if (current.pending?.opId !== result.opId && current.installedEnvelope?.opId !== result.opId) return;
    useAdiaStore.getState().publishPrayer(result.prayer, result);
  }).catch(() => { /* hydrate repairs failed observers; local save succeeded */ });
  void syncContentLifecycle().catch(() => {});
}

export async function savePrayerIntent(prayerId: string, intent: PrayerIntent,
  expectedAuth = prayerEditorAuth(), recoveredAudio: readonly RecoveredAudioCommitClaim[] = []): Promise<void> {
  const result = await new PrayerAuthoringSession(expectedAuth).save(prayerId, intent, recoveredAudio);
  if (result.kind !== 'committed') throw new Error('prayer-attempt-not-pending');
}
